import { computed, inject, Injectable, signal } from '@angular/core';
import { LOCAL_STORAGE_KEY_STYLE_LIBRARY } from '../const';
import { CosmeticOptions, DisplayOptions } from '../models/filter-models';
import { CosmeticsGeneratorService } from './cosmetics-generator.service';

export interface StyleLibraryEntry {
  id: string;
  name: string;
  options: CosmeticOptions;
  createdAt: number;
}

const LIBRARY_MAX_ENTRIES = 100;
const DEFAULT_COSMETIC_OPTIONS = new CosmeticOptions();

@Injectable({
  providedIn: 'root',
})
export class CosmeticStyleClipboardService {
  private readonly cosmeticsGeneratorService = inject(CosmeticsGeneratorService);

  private readonly clipboardSignal = signal<CosmeticOptions | null>(null);
  private readonly librarySignal = signal<StyleLibraryEntry[]>([]);

  readonly hasClipboard = computed(() => this.clipboardSignal() !== null);
  readonly hasLibraryEntries = computed(() => this.librarySignal().length > 0);

  getClipboardOptions(): CosmeticOptions | null {
    const clip = this.clipboardSignal();
    return clip ? new CosmeticOptions({ ...clip }) : null;
  }

  constructor() {
    this.loadLibrary();
  }

  getLibraryEntries(): StyleLibraryEntry[] {
    return this.librarySignal();
  }

  clearAllButLast() {
    const library = this.librarySignal();
    if (library.length <= 1) return;
    this.librarySignal.set([library[0]]);
    this.persistLibrary();
  }

  canCopy(options: CosmeticOptions | null | undefined): boolean {
    if (!options) return false;
    return !this.cosmeticOptionsEqual(options, DEFAULT_COSMETIC_OPTIONS);
  }

  copy(options: CosmeticOptions, nameHint?: string) {
    if (!this.canCopy(options)) return;

    const cloned = new CosmeticOptions({ ...options });
    this.clipboardSignal.set(cloned);

    let library = [...this.librarySignal()];
    const existing = library.find(e => this.cosmeticOptionsEqual(e.options, cloned));

    if (existing) {
      existing.createdAt = Date.now();
      library = [
        existing,
        ...library.filter(e => !this.cosmeticOptionsEqual(e.options, cloned)),
      ];
    } else {
      library.unshift({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        name: this.generateLibraryName(nameHint),
        options: new CosmeticOptions({ ...cloned }),
        createdAt: Date.now(),
      });
    }

    if (library.length > LIBRARY_MAX_ENTRIES) {
      library.length = LIBRARY_MAX_ENTRIES;
    }
    this.librarySignal.set(library);
    this.persistLibrary();
  }

  pasteTo(target: CosmeticOptions, source: CosmeticOptions) {
    Object.assign(target, new CosmeticOptions({ ...source }));
  }

  resolveOptionsForCopy(displayOptions: DisplayOptions, exampleName?: string, title?: string): CosmeticOptions {
    const result = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(displayOptions, exampleName, title);
    return new CosmeticOptions({ ...result.cosmeticOptions });
  }

  generateLibraryName(hint?: string): string {
    const trimmed = hint?.trim();
    if (trimmed && !this.librarySignal().some(e => e.name === trimmed)) {
      return trimmed;
    }

    let n = 1;
    while (this.librarySignal().some(e => e.name === `Custom style ${n}`)) {
      n++;
    }
    return `Custom style ${n}`;
  }

  private cosmeticOptionsEqual(a: CosmeticOptions, b: CosmeticOptions): boolean {
    const keys = new Set([
      ...(Object.keys(a) as (keyof CosmeticOptions)[]),
      ...(Object.keys(b) as (keyof CosmeticOptions)[]),
    ]);
    for (const key of keys) {
      if ((a[key] ?? undefined) !== (b[key] ?? undefined)) {
        return false;
      }
    }
    return true;
  }

  private loadLibrary() {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY_STYLE_LIBRARY);
      const parsed = raw ? JSON.parse(raw) as StyleLibraryEntry[] : [];
      this.librarySignal.set(
        parsed.map(e => ({
          ...e,
          options: new CosmeticOptions(e.options),
        }))
      );
    } catch {
      this.librarySignal.set([]);
    }
  }

  private persistLibrary() {
    localStorage.setItem(LOCAL_STORAGE_KEY_STYLE_LIBRARY, JSON.stringify(this.librarySignal()));
  }
}
